import React, { useCallback, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";

// Ô vẽ chữ ký (chuột + cảm ứng qua Pointer Events), nền trong suốt, nét mực tối.
// Canvas được scale theo devicePixelRatio để nét không bị nhoè trên màn retina.
// ref: { clear(), isEmpty(), toDataURL() } — toDataURL() trả PNG đã cắt sát nét vẽ.
const INK = "#0f172a";

const SignaturePad = React.forwardRef(({ height = 180, onChange }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const dirty = useRef(false);

  const setup = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const { width } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = INK;
    dirty.current = false;
  }, [height]);

  useEffect(() => {
    setup();
    // Đổi kích thước canvas sẽ xoá nét vẽ → chỉ dựng lại khi chưa vẽ gì.
    const onResize = () => { if (!dirty.current) setup(); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setup]);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = point(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fillStyle = INK;
    ctx.fill();
    if (!dirty.current) { dirty.current = true; onChange?.(false); }
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = point(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const onPointerUp = () => { drawing.current = false; last.current = null; };

  useImperativeHandle(ref, () => ({
    clear: () => { setup(); onChange?.(true); },
    isEmpty: () => !dirty.current,
    toDataURL: () => trimmedPng(canvasRef.current),
  }), [setup, onChange]);

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onPointerCancel={onPointerUp}
      className="tw-block tw-w-full tw-cursor-crosshair tw-rounded-geist-md tw-border tw-border-dashed tw-border-geist-border-2 tw-bg-white"
      style={{ height, touchAction: "none" }}
    />
  );
});

// Cắt bỏ vùng trống quanh nét vẽ (chừa lề 8px) để chữ ký in lên phiếu không bị nhỏ xíu.
function trimmedPng(canvas) {
  const { width, height } = canvas;
  const data = canvas.getContext("2d").getImageData(0, 0, width, height).data;
  let top = height, left = width, right = -1, bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] === 0) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return canvas.toDataURL("image/png");
  const pad = Math.round(8 * (window.devicePixelRatio || 1));
  const sx = Math.max(0, left - pad);
  const sy = Math.max(0, top - pad);
  const sw = Math.min(width, right + pad + 1) - sx;
  const sh = Math.min(height, bottom + pad + 1) - sy;
  const out = document.createElement("canvas");
  out.width = sw;
  out.height = sh;
  out.getContext("2d").drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
  return out.toDataURL("image/png");
}

SignaturePad.displayName = "SignaturePad";
SignaturePad.propTypes = {
  height: PropTypes.number,
  onChange: PropTypes.func,
};

export default SignaturePad;
